import { RIDE_STATUSES, USER_ROLES } from '../constants/constants';

export const FLEXIBLE_REDEEM_SCROLL_ID = 'flexible-ride-redeem-actions';
export const FLEXIBLE_REDEEM_SCROLL_QUERY = 'scrollToRedeem';

export const FLEXIBLE_UBER_REDEEM_ROLES = [
  USER_ROLES.HO_ADMIN,
  USER_ROLES.HO_EMPLOYEE,
  USER_ROLES.ENTERPRISE_ADMIN,
];

const isIsoLikeDateString = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);

const getFlexiblePickupDateTime = (ride) => {
  if (!ride) return null;

  if (ride.isPickUpDateFlexible) {
    if (ride.pickUpDateLowerLimit?.datetime) {
      return ride.pickUpDateLowerLimit.datetime;
    }

    if (isIsoLikeDateString(ride.pickUpDateLowerLimit)) {
      return ride.pickUpDateLowerLimit;
    }
  }

  if (ride.pickUpDate?.datetime) {
    return ride.pickUpDate.datetime;
  }

  if (isIsoLikeDateString(ride.pickUpDate)) {
    return ride.pickUpDate;
  }

  if (ride.tcEstimatedPickUpDate?.datetime) {
    return ride.tcEstimatedPickUpDate.datetime;
  }

  return null;
};

const getFlexibleRideTimezone = (ride) => {
  if (ride?.isPickUpDateFlexible) {
    return (
      ride?.pickUpDateLowerLimit?.timezone ??
      ride?.pickUpDate?.timezone ??
      ride?.tcEstimatedPickUpDate?.timezone ??
      ride?.timezone
    );
  }

  return (
    ride?.pickUpDate?.timezone ??
    ride?.pickUpDateLowerLimit?.timezone ??
    ride?.tcEstimatedPickUpDate?.timezone ??
    ride?.timezone
  );
};

const formatDayInTimezone = (date, timezone) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || !timezone) return null;

  const formatted = parsed.toLocaleDateString('en-CA', { timeZone: timezone });
  return /^\d{4}-\d{2}-\d{2}$/.test(formatted) ? formatted : null;
};

const addDaysToDayString = (dayStr, days) => {
  if (!dayStr || !/^\d{4}-\d{2}-\d{2}$/.test(dayStr)) return null;

  const [year, month, day] = dayStr.split('-').map(Number);
  const nextDate = new Date(Date.UTC(year, month - 1, day + days));
  if (Number.isNaN(nextDate.getTime())) return null;

  return nextDate.toISOString().slice(0, 10);
};

const getHourInTimezone = (date, timezone) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || !timezone) return null;

  return parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    }).format(parsed),
    10,
  );
};

export const isWithinFlexibleRedeemWindow = (ride) => {
  const pickupDateTime = getFlexiblePickupDateTime(ride);
  const timezone = getFlexibleRideTimezone(ride);
  if (!pickupDateTime || !timezone) return false;

  const now = new Date();
  const pickupDay = formatDayInTimezone(pickupDateTime, timezone);
  const today = formatDayInTimezone(now, timezone);
  if (!pickupDay || !today) return false;

  if (today === pickupDay) return true;

  const nextDay = addDaysToDayString(pickupDay, 1);
  if (!nextDay || today !== nextDay) return false;

  const hour = getHourInTimezone(now, timezone);
  return hour !== null && hour < 6;
};

export const canUserRedeemFlexibleRide = (role, isViewDashboardMode = false) => {
  if (role === USER_ROLES.SUPER_ADMIN) {
    return Boolean(isViewDashboardMode);
  }

  return FLEXIBLE_UBER_REDEEM_ROLES.includes(role);
};

export const isFlexibleUberRide = (ride) => {
  if (!ride || ride.vehicleType !== 'uberAmbulatoryRide' || !ride.isPickUpDateFlexible) {
    return false;
  }

  const isFlexible = ride?.uberInfo?.isFlexible ?? ride?.isPickUpDateFlexible;
  return Boolean(isFlexible);
};

const hasUberDriverAssigned = (ride) => {
  const driver = ride?.uberInfo?.driverInfo;
  return Boolean(driver?.name || driver?.phone || driver?.vehicleMake || driver?.vehicleModel);
};

const isUberStatusScheduled = (ride) =>
  String(ride?.uberInfo?.uberStatus || '').toLowerCase() === 'scheduled';

export const canEnableFlexibleRedeemButton = (ride) => {
  if (!isWithinFlexibleRedeemWindow(ride)) return false;
  if (ride?.status !== RIDE_STATUSES.ACCEPTED) return false;
  if (!isUberStatusScheduled(ride)) return false;

  return !hasUberDriverAssigned(ride);
};

export const shouldShowFlexibleRedeemButton = (ride, role, isViewDashboardMode = false) =>
  canUserRedeemFlexibleRide(role, isViewDashboardMode) &&
  isFlexibleUberRide(ride) &&
  canEnableFlexibleRedeemButton(ride);

export const shouldShowFlexibleRedeemPanel = (
  ride,
  role,
  isViewDashboardMode = false,
  redeemBlockMessage = null,
) => {
  if (!canUserRedeemFlexibleRide(role, isViewDashboardMode)) return false;
  if (!isFlexibleUberRide(ride)) return false;
  if (!isWithinFlexibleRedeemWindow(ride)) return false;

  if (canEnableFlexibleRedeemButton(ride)) return true;

  if (
    redeemBlockMessage &&
    ride?.status === RIDE_STATUSES.ACCEPTED &&
    isUberStatusScheduled(ride) &&
    !hasUberDriverAssigned(ride)
  ) {
    return true;
  }

  return false;
};

export const buildFlexibleRedeemRideDetailsUrl = (rideId) =>
  `/rides/${rideId}?${FLEXIBLE_REDEEM_SCROLL_QUERY}=1`;
